<template>
  <CBox class="network-ai-chat">
    <CFlex class="network-ai-chat__panel">
      <CBox
        ref="messagesContainer"
        class="network-ai-chat__body"
        :class="{ 'network-ai-chat__body--conversation': haveMessages || isLoading }"
      >
        <CFlex v-if="!haveMessages && !isLoading" class="network-ai-chat__home">
          <CFlex class="network-ai-chat__logo">
            <CBox as="img" class="network-ai-chat__logo-image" :src="omintLogoSrc" alt="Omint" />
          </CFlex>

          <OmtTypography as="h1-bold" class="network-ai-chat__title">Como posso ajudar?</OmtTypography>
          <OmtTypography as="p1" class="network-ai-chat__subtitle">
            Fale o profissional ou estabelecimento que deseja encontrar.
          </OmtTypography>

          <CFlex class="network-ai-chat__composer network-ai-chat__composer--home">
            <OmtTextarea
              ref="textarea"
              class="network-ai-chat__textarea"
              :initialValue="observation"
              placeholder="Ex.: Quero um cardiologista próximo de mim"
              @change="handleChange"
              @keydown.native="handleTextareaKeydown"
            />

            <OmtButton
              v-if="!hasDraft && !isRecording"
              class="network-ai-chat__icon-button"
              type="button"
              title="Usar voz"
              @click="startRecording"
            >
              <OmtIcons name="microphone" color="#0047A1" />
            </OmtButton>

            <OmtButton
              v-else-if="isRecording"
              class="network-ai-chat__icon-button"
              type="button"
              title="Parar gravação"
              @click="stopRecording"
            >
              <OmtIcons name="xmark" color="#0047A1" />
            </OmtButton>

            <OmtButton
              v-else
              class="network-ai-chat__icon-button"
              type="button"
              title="Enviar"
              :isDisabled="isLoading"
              @click="handleSend"
            >
              <OmtIcons name="paper-plane-top" color="#0047A1" />
            </OmtButton>
          </CFlex>

          <CBox class="network-ai-chat__suggestions">
            <OmtTypography as="p2" class="network-ai-chat__suggestions-title">Sugestões de busca</OmtTypography>
            <CFlex class="network-ai-chat__suggestions-list">
              <CBox
                v-for="suggestion in suggestions"
                :key="suggestion.title"
                as="button"
                type="button"
                class="network-ai-chat__suggestion"
                @click="applySuggestion(suggestion.text)"
              >
                <CBox as="span" class="network-ai-chat__suggestion-icon">
                  <OmtIcons :name="suggestion.icon" color="#0047A1" size="sm" />
                </CBox>
                <span>{{ suggestion.title }}</span>
              </CBox>
            </CFlex>
          </CBox>
        </CFlex>

        <CBox v-else class="network-ai-chat__messages">
          <CFlex
            v-for="(message, index) in messages"
            :key="message.id"
            class="network-ai-chat__message-row"
            :class="{
              'network-ai-chat__message-row--user': message.from === 'user',
              'network-ai-chat__message-row--providers': message.providers
            }"
          >
            <CFlex v-if="message.from !== 'user' && shouldShowBotAvatar(index)" class="network-ai-chat__avatar">
              <CBox as="img" class="network-ai-chat__logo-image" :src="omintLogoSrc" alt="" />
            </CFlex>
            <CBox v-else-if="message.from !== 'user'" class="network-ai-chat__avatar-spacer" aria-hidden="true" />

            <CBox v-if="message.providers" class="network-ai-chat__providers">
              <CBox v-for="provider in message.providers" :key="provider.id" class="network-ai-chat__provider-card">
                <CBox class="network-ai-chat__provider-content">
                  <CFlex class="network-ai-chat__provider-main">
                    <CFlex class="network-ai-chat__provider-icon">
                      <OmtIcons name="user-doctor-hair" color="#0047A1" size="2x" />
                    </CFlex>

                    <CBox class="network-ai-chat__provider-info">
                      <CBox class="network-ai-chat__provider-heading">
                        <OmtTypography as="h3-bold" class="network-ai-chat__provider-name">
                          {{ provider.name }}
                        </OmtTypography>
                        <OmtTypography
                          v-if="provider.specialties"
                          as="p2"
                          class="network-ai-chat__provider-specialties"
                        >
                          {{ provider.specialties }}
                        </OmtTypography>
                      </CBox>
                    </CBox>
                  </CFlex>

                  <CBox class="network-ai-chat__provider-details">
                    <CBox v-if="provider.network" class="network-ai-chat__provider-network">
                      {{ provider.network }}
                    </CBox>

                    <CFlex v-if="provider.address" class="network-ai-chat__provider-detail-list">
                      <CBox as="span" class="network-ai-chat__provider-detail">
                        <OmtIcons
                          class="network-ai-chat__provider-detail-icon"
                          name="map-marker-alt"
                          color="#111827"
                          size="inherit"
                        />
                        <span>{{ provider.address }}</span>
                      </CBox>
                    </CFlex>

                    <CFlex v-if="provider.phones.length" class="network-ai-chat__provider-detail-list">
                      <CBox
                        v-for="(phone, phoneIndex) in provider.phones"
                        :key="`${provider.id}-phone-${phoneIndex}`"
                        as="span"
                        class="network-ai-chat__provider-detail"
                      >
                        <OmtIcons
                          class="network-ai-chat__provider-detail-icon"
                          :name="provider.hasWhatsapp ? 'whatsapp' : 'phone-alt'"
                          :color="provider.hasWhatsapp ? '#008A45' : '#111827'"
                          size="inherit"
                        />
                        {{ phone }}
                      </CBox>
                    </CFlex>

                    <CFlex v-if="provider.languages" class="network-ai-chat__provider-detail">
                      <OmtIcons
                        class="network-ai-chat__provider-detail-icon"
                        name="microphone"
                        prefix="fas"
                        color="#111827"
                        size="inherit"
                      />
                      <span>{{ provider.languages }}</span>
                    </CFlex>

                    <CFlex v-if="provider.teleconsultation" class="network-ai-chat__provider-detail">
                      <OmtIcons
                        class="network-ai-chat__provider-detail-icon"
                        name="video"
                        color="#111827"
                        size="inherit"
                      />
                      <span>Teleconsulta</span>
                    </CFlex>
                  </CBox>
                </CBox>

                <CFlex class="network-ai-chat__provider-actions">
                  <CBox
                    v-if="provider.primaryPhoneLink"
                    as="a"
                    :href="provider.primaryPhoneLink"
                    class="network-ai-chat__provider-action"
                    title="Telefone"
                  >
                    <OmtIcons name="phone-alt" color="#0047A1" size="inherit" font-size="1.55em" />
                  </CBox>
                  <CBox v-else class="network-ai-chat__provider-action network-ai-chat__provider-action--disabled">
                    <OmtIcons name="phone-alt" color="#0047A1" size="inherit" font-size="1.55em" />
                  </CBox>

                  <CBox
                    v-if="provider.whatsappLink"
                    as="a"
                    :href="provider.whatsappLink"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="network-ai-chat__provider-action"
                    title="WhatsApp"
                  >
                    <OmtIcons name="whatsapp" color="#008A45" size="inherit" font-size="1.55em" />
                  </CBox>
                  <CBox
                    v-else
                    class="network-ai-chat__provider-action network-ai-chat__provider-action--disabled"
                    title="WhatsApp"
                  >
                    <OmtIcons name="whatsapp" color="#008A45" size="inherit" font-size="1.55em" />
                  </CBox>

                  <CBox
                    v-if="provider.mapLink"
                    as="a"
                    :href="provider.mapLink"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="network-ai-chat__provider-action"
                    title="Rota"
                  >
                    <OmtIcons name="paper-plane-top" color="#0047A1" size="inherit" font-size="1.55em" />
                  </CBox>
                  <CBox v-else class="network-ai-chat__provider-action network-ai-chat__provider-action--disabled">
                    <OmtIcons name="paper-plane-top" color="#0047A1" size="inherit" font-size="1.55em" />
                  </CBox>
                </CFlex>
              </CBox>
            </CBox>

            <CBox v-else class="network-ai-chat__message-content">
              <CBox
                class="network-ai-chat__bubble"
                :class="message.from === 'user' ? 'network-ai-chat__bubble--user' : 'network-ai-chat__bubble--bot'"
              >
                <PreviewFile
                  v-if="message.attachment"
                  :attachment="message.attachment"
                  :bg="message.from === 'user' ? '#E8F0FF' : '#ffffff'"
                  :maxW="['100%', '520px']"
                />

                <template v-else>
                  <OmtTypography
                    as="p1"
                    class="network-ai-chat__message-text"
                    :class="
                      message.from === 'user'
                        ? 'network-ai-chat__message-text--user'
                        : 'network-ai-chat__message-text--bot'
                    "
                  >
                    {{ message.text }}
                  </OmtTypography>
                  <OmtTypography
                    as="p2"
                    class="network-ai-chat__message-time"
                    :class="{ 'network-ai-chat__message-time--user': message.from === 'user' }"
                  >
                    {{ message.time }}
                    <CBox
                      v-if="message.from === 'user'"
                      as="span"
                      class="network-ai-chat__message-check"
                      aria-hidden="true"
                    >
                      ✓✓
                    </CBox>
                  </OmtTypography>
                </template>
              </CBox>

              <CFlex v-if="message.options && message.options.length" class="network-ai-chat__quick-replies">
                <CBox
                  v-for="option in message.options"
                  :key="`${message.id}-${option.value}`"
                  as="button"
                  type="button"
                  class="network-ai-chat__quick-reply"
                  :disabled="isLoading"
                  @click="sendQuickReply(option.value)"
                >
                  {{ option.label }}
                </CBox>
              </CFlex>
            </CBox>
          </CFlex>

          <CFlex v-if="isLoading" class="network-ai-chat__loading-row">
            <CBox class="network-ai-chat__loading-bubble" role="status" aria-live="polite">
              <OmtTypography as="p1" class="network-ai-chat__loading-text">
                Aguarde por favor
                <span class="loading-dots" aria-hidden="true">
                  <span class="loading-dot loading-dot--1"></span>
                  <span class="loading-dot loading-dot--2"></span>
                  <span class="loading-dot loading-dot--3"></span>
                </span>
              </OmtTypography>
            </CBox>
          </CFlex>
        </CBox>
      </CBox>

      <CBox v-if="haveMessages || isLoading" class="network-ai-chat__footer">
        <PreviewFile
          v-if="selectedFile"
          wrap
          mb="12px"
          :file="selectedFile"
          removable
          bg="#F8FAFC"
          border="1px solid #E2E8F0"
          @remove="removeSelectedFile"
        />

        <CFlex class="network-ai-chat__composer">
          <OmtTextarea
            ref="textarea"
            class="network-ai-chat__textarea"
            :initialValue="observation"
            placeholder="Digite sua mensagem..."
            @change="handleChange"
            @keydown.native="handleTextareaKeydown"
          />

          <CFlex class="network-ai-chat__actions">
            <input ref="fileInput" type="file" hidden @change="handleFileSelected" />

            <OmtButton
              v-if="!hasDraft && !isRecording"
              class="network-ai-chat__icon-button"
              type="button"
              title="Usar voz"
              @click="startRecording"
            >
              <OmtIcons name="microphone" color="#0047A1" />
            </OmtButton>

            <OmtButton
              v-else-if="isRecording"
              class="network-ai-chat__icon-button"
              type="button"
              title="Parar gravação"
              @click="stopRecording"
            >
              <OmtIcons name="xmark" color="#0047A1" />
            </OmtButton>

            <OmtButton
              v-else
              class="network-ai-chat__icon-button"
              type="button"
              title="Enviar"
              :isDisabled="isLoading"
              @click="handleSend"
            >
              <OmtIcons name="paper-plane-top" color="#0047A1" />
            </OmtButton>
          </CFlex>
        </CFlex>
      </CBox>
    </CFlex>
  </CBox>
</template>

<script>
import { CBox, CFlex } from "@chakra-ui/vue";
import { BuscarPrestadorViaChat } from "@/api/redeCredenciada";
import PreviewFile from "./components/preview-file.vue";

function cleanProviderValue(value) {
  return `${value || ""}`.replace(/\s+/g, " ").trim();
}

function cleanMessageText(value) {
  return `${value || ""}`.replace(/\r\n?/g, "\n").trim();
}

function formatAddressWord(word) {
  const lower = word.toLowerCase();
  if (["da", "de", "do", "das", "dos"].includes(lower)) return lower;
  if (lower.length === 2) return lower.toUpperCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function formatAddressPart(value) {
  return cleanProviderValue(value).split(" ").filter(Boolean).map(formatAddressWord).join(" ");
}

function formatProviderAddress(record) {
  const rua = formatProviderList([record?.tp_logradouro, record?.en_rua], " ");
  const numero = cleanProviderValue(record?.en_numero);
  const complemento = formatAddressPart(record?.en_complemento);
  const bairro = formatAddressPart(record?.en_bairro);
  const cidade = formatAddressPart(record?.en_cidade);
  const estado = cleanProviderValue(record?.cd_estado).toUpperCase();
  const endereco = [rua, numero].filter(Boolean).join(", ");
  const cidadeEstado = [cidade, estado].filter(Boolean).join(" / ");

  return [endereco, complemento, bairro, cidadeEstado].filter(Boolean).join(" - ");
}

function formatProviderList(values, separator = " - ") {
  return values
    .map(cleanProviderValue)
    .filter(value => value && value !== "-")
    .join(separator);
}

function formatProviderPhones(value) {
  return Array.isArray(value) ? value.map(cleanProviderValue).filter(Boolean) : [];
}

function buildPhoneLink(phone) {
  const digits = `${phone || ""}`.replace(/\D/g, "");
  return digits ? `tel:${digits}` : "";
}

function buildWhatsAppLink(phone) {
  const digits = `${phone || ""}`.replace(/\D/g, "").replace(/^0+/, "");
  if (!digits) return "";

  return `https://api.whatsapp.com/send?phone=${digits.startsWith("55") ? digits : `55${digits}`}`;
}

function isPositiveProviderValue(value) {
  return ["s", "sim", "true", "1", "yes"].includes(cleanProviderValue(value).toLowerCase());
}

function getProviderRecords(response) {
  return response?.Data?.recordset || [];
}

function getProviderReply(response) {
  return cleanMessageText(response?.Data?.reply || "");
}

function splitProviderReply(reply) {
  const text = cleanMessageText(reply);
  const intro = text.split(/(?:^|\n)\s*1\.\s*Nome\s*:/i)[0];
  const footer = text.match(/(?:Há|Ha) mais resultados[\s\S]*$/i)?.[0] || "";
  return {
    intro: intro.trim(),
    footer: footer.trim()
  };
}

function mapProviderRecord(record, index) {
  const phones = formatProviderPhones(record?.telefones);
  const hasWhatsapp = cleanProviderValue(record?.cd_tipo_telefone).toUpperCase() === "WHT";
  const whatsappPhone = phones[0];
  const latitude = cleanProviderValue(record?.gm_lat);
  const longitude = cleanProviderValue(record?.gm_long);
  const mapLink =
    latitude && longitude ? `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}` : "";
  const code = cleanProviderValue(record?.cd_prestador);
  const name = cleanProviderValue(record?.ds_abreviado);

  if (!name) return null;

  return {
    id: code || `${index}-${name}`,
    name,
    code,
    specialties: formatProviderList([record?.ds_especialidade, record?.ds_subespecialidade]),
    network: cleanProviderValue(record?.cd_rede),
    address: formatProviderAddress(record),
    phones,
    primaryPhoneLink: buildPhoneLink(phones[0]),
    hasWhatsapp,
    whatsappPhone: hasWhatsapp ? whatsappPhone : "",
    whatsappLink: hasWhatsapp ? buildWhatsAppLink(whatsappPhone) : "",
    teleconsultation: isPositiveProviderValue(record?.possui_teleconferencia),
    languages: formatProviderList([record?.idiomas], ", "),
    mapLink
  };
}

export default {
  name: "Chat",
  components: {
    CBox,
    CFlex,
    PreviewFile
  },
  data() {
    return {
      omintLogoSrc: `${process.env.VUE_APP_BASE_URL}images/omintLogo.jpeg`,
      observation: "",
      messages: [],
      isLoading: false,
      selectedFile: null,
      isRecording: false,
      speechRecognition: null,
      suggestions: [
        {
          title: "Cardiologista",
          icon: "heartbeat",
          text: "Quero um cardiologista próximo de mim"
        },
        {
          title: "Dermatologista",
          icon: "stethoscope",
          text: "Quero um dermatologista próximo de mim"
        },
        {
          title: "Pediatra",
          icon: "baby-carriage",
          text: "Quero um pediatra próximo de mim"
        },
        {
          title: "Hospital",
          icon: "hospital",
          text: "Quero um hospital próximo de mim"
        },
        {
          title: "Laboratório",
          icon: "flask",
          text: "Quero um laboratório próximo de mim"
        }
      ]
    };
  },
  computed: {
    haveMessages() {
      return this.messages.length > 0;
    },
    hasDraft() {
      return !!(this.observation && this.observation.trim()) || !!this.selectedFile;
    }
  },
  watch: {
    messages() {
      this.scrollToBottom();
    },
    isLoading() {
      this.scrollToBottom();
    }
  },
  mounted() {
    this.$root.$emit("hide-loading");
    this.scrollToBottom();
  },
  beforeDestroy() {
    this.stopRecording();
    this.clearAttachmentMessagePreviewUrls();
  },
  methods: {
    shouldShowBotAvatar(index) {
      return index === 0 || this.messages[index - 1]?.from === "user";
    },
    getNowTime() {
      const date = new Date();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${hours}:${minutes}`;
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const ref = this.$refs.messagesContainer;
        const el = ref && (ref.$el || ref);
        if (!el) return;
        el.scrollTop = el.scrollHeight;
      });
    },
    clearAttachmentMessagePreviewUrls() {
      (this.messages || []).forEach(message => {
        const url = message?.attachment?.previewUrl;
        if (url) URL.revokeObjectURL(url);
      });
    },
    getFileExtensionFromName(name) {
      const fileName = (name || "").trim();
      const extensionIndex = fileName.lastIndexOf(".");
      if (extensionIndex === -1) return "ARQ";
      const extension = fileName.slice(extensionIndex + 1).trim();
      return (extension || "ARQ").slice(0, 6).toUpperCase();
    },
    setSelectedFile(file) {
      this.selectedFile = file || null;
    },
    removeSelectedFile() {
      this.setSelectedFile(null);
      const input = this.$refs.fileInput;
      const el = input && (input.$el || input);
      if (el) el.value = "";
    },
    pushBotMessage(text, options = []) {
      const message = cleanMessageText(text);
      if (!message) return;

      this.messages.push({
        id: `m_${Date.now()}_${Math.random().toString(16).slice(2)}_bot`,
        from: "system",
        text: message,
        time: this.getNowTime(),
        options
      });
    },
    pushProviderCards(records) {
      const providers = records.map(mapProviderRecord).filter(Boolean);
      if (!providers.length) return false;

      this.messages.push({
        id: `m_${Date.now()}_${Math.random().toString(16).slice(2)}_providers`,
        from: "system",
        text: "",
        time: this.getNowTime(),
        providers
      });

      return true;
    },
    async fileToDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    async handleSend() {
      if (!this.observation && !this.selectedFile) return;

      const sendingFile = this.selectedFile;
      const now = Date.now();
      const time = this.getNowTime();
      const messageText = this.observation;

      this.isLoading = true;

      if (sendingFile) {
        const file = sendingFile;
        const ext = this.getFileExtensionFromName(file.name).toLowerCase();
        const isImage =
          (!!file.type && file.type.startsWith("image/")) ||
          ["png", "jpg", "jpeg", "webp", "gif", "bmp", "heic", "heif"].includes(ext);
        const previewUrl = isImage ? URL.createObjectURL(file) : "";
        this.messages.push({
          id: `m_${now}_file`,
          from: "user",
          text: "",
          time,
          attachment: {
            name: file.name,
            type: file.type || "",
            extension: this.getFileExtensionFromName(file.name),
            previewUrl
          }
        });
      }

      if (messageText) {
        this.messages.push({
          id: `m_${now}`,
          from: "user",
          text: messageText,
          time
        });
      }

      this.observation = "";
      this.scrollToBottom();

      try {
        let imageDataUrl = null;
        if (sendingFile) {
          imageDataUrl = await this.fileToDataUrl(sendingFile);
        }

        const response = await BuscarPrestadorViaChat({
          message: messageText || "",
          imageDataUrl
        });

        this.isLoading = false;

        const reply = getProviderReply(response);
        const records = getProviderRecords(response);
        const options = response?.Data?.options || [];
        const providerReply = records.length ? splitProviderReply(reply) : { intro: reply, footer: "" };
        let hasProviderCards = false;

        if (providerReply.intro) this.pushBotMessage(providerReply.intro, options);
        if (records.length) hasProviderCards = this.pushProviderCards(records);
        if (providerReply.footer) this.pushBotMessage(providerReply.footer);
        if (!providerReply.intro && !hasProviderCards) {
          this.pushBotMessage(
            reply || "Não foi possível processar sua mensagem no momento. Tente novamente em instantes."
          );
        }

        this.scrollToBottom();
      } catch (error) {
        console.error(error);
        this.isLoading = false;
        this.pushBotMessage("Ocorreu um erro ao enviar sua mensagem. Verifique sua conexão e tente novamente.");
        this.scrollToBottom();
      } finally {
        this.removeSelectedFile();
      }
    },
    handleChange(text) {
      this.observation = text;
    },
    handleTextareaKeydown(event) {
      if (event.key === "Enter") {
        if (event.ctrlKey) {
          return;
        } else {
          event.preventDefault();
          this.handleSend();
        }
      }
    },
    applySuggestion(text) {
      this.observation = text;
    },
    sendQuickReply(text) {
      if (this.isLoading) return;

      this.observation = text;
      this.$nextTick(() => this.handleSend());
    },
    handleFileSelected(event) {
      const selectedFile = event && event.target && event.target.files ? event.target.files[0] : null;
      if (selectedFile) this.setSelectedFile(selectedFile);

      if (event && event.target) event.target.value = null;
    },
    startRecording() {
      try {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
          alert("Seu navegador não suporta reconhecimento de voz.");
          return;
        }

        if (this.isRecording) return;

        this.speechRecognition = new SpeechRecognition();
        this.speechRecognition.lang = "pt-BR";
        this.speechRecognition.interimResults = true;
        this.speechRecognition.maxAlternatives = 1;
        this.isRecording = true;

        let finalTranscript = this.observation || "";

        this.speechRecognition.onresult = event => {
          let interimTranscript = "";
          for (let resultIndex = event.resultIndex; resultIndex < event.results.length; resultIndex++) {
            const transcript = event.results[resultIndex][0].transcript;
            if (event.results[resultIndex].isFinal) {
              finalTranscript += (finalTranscript ? " " : "") + transcript;
            } else {
              interimTranscript += transcript;
            }
          }
          this.observation = finalTranscript + (interimTranscript ? ` ${interimTranscript}` : "");
        };

        this.speechRecognition.onerror = () => {
          this.isRecording = false;
        };

        this.speechRecognition.onend = () => {
          this.isRecording = false;
          this.speechRecognition = null;
        };

        this.speechRecognition.start();
      } catch (err) {
        console.error(err);
        alert(err.message || "Falha ao iniciar reconhecimento de voz.");
        this.isRecording = false;
      }
    },
    stopRecording() {
      try {
        if (this.speechRecognition) {
          this.speechRecognition.stop();
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isRecording = false;
        this.speechRecognition = null;
      }
    }
  }
};
</script>

<style scoped>
.network-ai-chat {
  width: 100%;
  min-height: calc(100vh - 72px);
  padding: 0;
  color: #111827;
  background: #ffffff;
}

.network-ai-chat__panel {
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  min-height: calc(100vh - 72px);
  margin: 0 auto;
  overflow: hidden;
  background: #ffffff;
}

.network-ai-chat__body {
  flex: 1;
  overflow-y: auto;
  padding: 48px 32px 16px;
}

.network-ai-chat__body--conversation {
  padding: 32px 16px 16px;
}

.network-ai-chat__home {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: calc(100vh - 252px);
  text-align: center;
}

.network-ai-chat__logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  margin: 24px 0 20px;
  overflow: hidden;
}

.network-ai-chat__logo-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.network-ai-chat__title {
  margin: 0 0 8px;
  color: #111827;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
}

.network-ai-chat__subtitle {
  max-width: 260px;
  margin: 0 auto;
  color: #4a5e77;
  font-size: 14px;
  line-height: 20px;
}

.network-ai-chat__composer {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 48px;
  padding: 0 8px 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  background: #ffffff;
}

.network-ai-chat__composer--home {
  min-height: 60px;
  margin-top: 40px;
  padding: 10px 12px;
  color: #4a5e77;
  text-align: left;
}

.network-ai-chat__textarea {
  flex: 1;
  height: 48px;
  min-height: 48px;
  max-height: 96px;
  padding: 12px 8px;
  resize: none;
  border: 0;
  border-radius: 12px;
  background: transparent;
  box-shadow: none;
  font-size: 14px;
  line-height: 20px;
}

.network-ai-chat__textarea:focus {
  border-color: transparent;
  box-shadow: none;
}

.network-ai-chat__icon-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  min-width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
}

.network-ai-chat__suggestions {
  width: 100%;
  max-width: 100%;
  margin-top: 40px;
  text-align: left;
}

.network-ai-chat__suggestions-title {
  display: block;
  margin: 0 0 12px;
  color: #111827;
  font-size: 12px;
  font-weight: 700;
}

.network-ai-chat__suggestions-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.network-ai-chat__suggestion {
  display: inline-flex;
  align-items: center;
  min-height: 36px;
  padding: 8px 12px;
  border: 1px solid #edf1f7;
  border-radius: 12px;
  color: #0047a1;
  background: #f8fafc;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  cursor: pointer;
}

.network-ai-chat__suggestion:active {
  transform: translateY(1px);
}

.network-ai-chat__suggestion-icon {
  display: inline-flex;
  align-items: center;
  min-width: 14px;
  margin-right: 8px;
}

.network-ai-chat__messages {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  padding-bottom: 16px;
}

.network-ai-chat__message-row {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  padding-right: 40px;
}

.network-ai-chat__message-row--user {
  align-items: flex-end;
  justify-content: flex-end;
  padding-right: 0;
  padding-left: 64px;
}

.network-ai-chat__message-row--providers {
  padding-right: 0;
}

.network-ai-chat__message-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 100%;
}

.network-ai-chat__message-row--user .network-ai-chat__message-content {
  align-items: flex-end;
}

.network-ai-chat__avatar,
.network-ai-chat__avatar-spacer {
  width: 32px;
  min-width: 32px;
  height: 32px;
  margin-right: 8px;
}

.network-ai-chat__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.network-ai-chat__bubble {
  max-width: 100%;
  padding: 12px;
  border-radius: 12px;
  word-break: break-word;
  text-align: left;
}

.network-ai-chat__bubble--bot {
  border: 1px solid #e5e7eb;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.04);
}

.network-ai-chat__bubble--user {
  border: 0;
  background: #e8f0ff;
}

.network-ai-chat__providers {
  display: grid;
  width: min(100%, 680px);
  gap: 12px;
}

.network-ai-chat__provider-card {
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.04);
  text-align: left;
}

.network-ai-chat__provider-content {
  padding: 16px 16px 0;
}

.network-ai-chat__provider-main {
  align-items: center;
}

.network-ai-chat__provider-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  min-width: 56px;
  height: 56px;
  margin-right: 12px;
  border-radius: 50%;
  background: #f3f4f6;
}

.network-ai-chat__provider-info {
  flex: 1;
  min-width: 0;
}

.network-ai-chat__provider-heading {
  min-width: 0;
}

.network-ai-chat__provider-name {
  display: -webkit-box;
  margin: 0 0 2px;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: #111827;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
}

.network-ai-chat__provider-specialties {
  margin: 0;
  color: #111827;
  font-size: 12px;
  line-height: 16px;
}

.network-ai-chat__provider-network {
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  margin-top: 0;
  padding: 3px 8px;
  border-radius: 8px;
  color: #0047a1;
  background: #e8f0ff;
  font-size: 11px;
  font-weight: 700;
  line-height: 14px;
}

.network-ai-chat__provider-details {
  margin-top: 8px;
}

.network-ai-chat__provider-detail,
.network-ai-chat__provider-detail-list {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 8px;
  color: #111827;
  font-size: 12px;
  line-height: 18px;
}

.network-ai-chat__provider-detail-list {
  flex-wrap: wrap;
  column-gap: 12px;
}

.network-ai-chat__provider-detail-list .network-ai-chat__provider-detail {
  margin-top: 0;
}

.network-ai-chat__provider-detail-icon {
  width: 18px;
  min-width: 18px;
  height: 18px;
  flex-shrink: 0;
  font-size: 18px;
  line-height: 18px;
}

.network-ai-chat__provider-detail-icon svg {
  width: 18px;
  height: 18px;
}

.network-ai-chat__provider-actions {
  align-items: center;
  height: 44px;
  margin-top: 12px;
  border-top: 1px solid #e5e7eb;
}

.network-ai-chat__provider-action {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 44px;
  text-decoration: none;
}

.network-ai-chat__provider-action + .network-ai-chat__provider-action {
  border-left: 1px solid #e5e7eb;
}

.network-ai-chat__provider-action--disabled {
  opacity: 0.45;
  pointer-events: none;
}

.network-ai-chat__message-text {
  margin: 0;
  white-space: pre-wrap;
}

.network-ai-chat__message-text--bot {
  color: #111827;
}

.network-ai-chat__message-text--user {
  color: #0047a1;
}

.network-ai-chat__message-time {
  margin: 8px 0 0;
  color: #4a5e77;
  font-size: 11px;
  line-height: 14px;
}

.network-ai-chat__message-time--user {
  text-align: right;
}

.network-ai-chat__message-check {
  margin-left: 4px;
  color: #1e8ee5;
  font-size: 10px;
}

.network-ai-chat__quick-replies {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.network-ai-chat__quick-reply {
  min-height: 36px;
  padding: 8px 12px;
  border: 1px solid #edf1f7;
  border-radius: 12px;
  color: #0047a1;
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0, 71, 161, 0.12);
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  cursor: pointer;
}

.network-ai-chat__quick-reply:disabled {
  cursor: default;
  opacity: 0.55;
}

.network-ai-chat__loading-row {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
}

.network-ai-chat__loading-bubble {
  min-height: 36px;
  margin-top: 28px;
  padding: 10px 12px;
  border-radius: 18px;
  color: #111827;
  background: #f3f4f6;
}

.network-ai-chat__loading-text {
  margin: 0;
}

.network-ai-chat__footer {
  position: sticky;
  bottom: 0;
  padding: 12px 32px calc(16px + env(safe-area-inset-bottom));
  background: #ffffff;
}

.network-ai-chat__actions {
  display: flex;
  align-items: center;
  margin-left: 8px;
}

.loading-dots {
  display: inline-flex;
  align-items: flex-end;
  gap: 4px;
  margin-left: 12px;
  vertical-align: middle;
}

.loading-dot {
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  animation: loading-dot-bounce 900ms ease-in-out infinite;
}

.loading-dot--1 {
  background: #64748b;
  animation-delay: 0ms;
}

.loading-dot--2 {
  background: #475569;
  animation-delay: 120ms;
}

.loading-dot--3 {
  background: #334155;
  animation-delay: 240ms;
}

@media (min-width: 768px) {
  .network-ai-chat {
    padding: 24px 0;
    background: #f7f9fc;
  }

  .network-ai-chat__panel {
    width: calc(100% - 48px);
    max-width: 640px;
    min-height: calc(100vh - 128px);
    border: 1px solid #edf1f7;
    border-radius: 16px;
    box-shadow: 0 18px 48px rgba(16, 24, 40, 0.08);
  }

  .network-ai-chat__body--conversation {
    padding-right: 24px;
    padding-left: 24px;
  }

  .network-ai-chat__home {
    min-height: calc(100vh - 336px);
  }

  .network-ai-chat__message-text {
    font-size: 16px;
    line-height: 22px;
  }

  .network-ai-chat__footer {
    padding-bottom: 24px;
  }
}

@media (min-width: 1200px) {
  .network-ai-chat {
    padding-right: 80px;
    padding-left: 80px;
  }

  .network-ai-chat__panel {
    max-width: 1104px;
  }

  .network-ai-chat__body--conversation {
    padding-right: 48px;
    padding-left: 48px;
  }

  .network-ai-chat__footer {
    padding-right: 48px;
    padding-left: 48px;
  }
}

@keyframes loading-dot-bounce {
  0%,
  60%,
  100% {
    transform: translateY(0);
    opacity: 0.7;
  }

  30% {
    transform: translateY(-5px);
    opacity: 1;
  }
}
</style>
